import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  Send,
  Flame,
  Trophy,
  Star,
  Sparkles,
  MoreHorizontal,
  Flag,
  User,
  Ban,
  AlertTriangle
} from "lucide-react";
import { ShareButton } from "./ShareFeatures";

// Post type icons
const POST_TYPE_ICONS = {
  reflection: MessageCircle,
  milestone: Trophy,
  badge: Star,
  streak: Flame,
  encouragement: Sparkles
};

const POST_TYPE_COLORS = {
  reflection: "text-blue-500 bg-blue-500/10",
  milestone: "text-amber-500 bg-amber-500/10",
  badge: "text-purple-500 bg-purple-500/10",
  streak: "text-orange-500 bg-orange-500/10",
  encouragement: "text-pink-500 bg-pink-500/10"
};

// Report reason options
const REPORT_REASONS = [
  { value: "spam", label: "Spam", description: "Advertising, promotional content, or repetitive posts" },
  { value: "harassment", label: "Harassment", description: "Bullying, threats, or targeted attacks" },
  { value: "inappropriate", label: "Inappropriate Content", description: "Explicit, offensive, or disturbing content" },
  { value: "misinformation", label: "Misinformation", description: "False or misleading information" },
  { value: "other", label: "Other", description: "Something else not listed above" }
];

// Single community post
export function CommunityPost({ post, onLike, onReport, onBlock, currentUserId }) {
  const [liked, setLiked] = useState(post.liked_by_user || false);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [showMenu, setShowMenu] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDetails, setReportDetails] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const PostIcon = POST_TYPE_ICONS[post.post_type] || MessageCircle;
  const iconColors = POST_TYPE_COLORS[post.post_type] || "text-gray-500 bg-gray-500/10";
  
  const isOwnPost = currentUserId === post.user_id;
  
  // Memoize share data to prevent unnecessary re-renders
  const shareData = useMemo(() => ({ text: post.content }), [post.content]);
  
  const handleLike = async () => {
    const newLiked = !liked;
    setLiked(newLiked);
    setLikeCount(prev => newLiked ? prev + 1 : prev - 1);
    
    try {
      await onLike(post.post_id, newLiked);
    } catch (error) {
      // Revert on error
      setLiked(!newLiked);
      setLikeCount(prev => newLiked ? prev - 1 : prev + 1);
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to toggle like:', error);
      }
    }
  };
  
  const handleReport = async () => {
    if (!reportReason) {
      toast.error("Please select a reason for your report");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onReport(post.post_id, reportReason, reportDetails);
      setShowReportDialog(false);
      setReportReason("");
      setReportDetails("");
      toast.success("Post reported. Thank you for helping keep our community safe.");
    } catch (error) {
      toast.error("Failed to report post");
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to report post:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleBlock = async () => {
    setIsSubmitting(true);
    try {
      await onBlock(post.user_id, post.author_name);
      setShowBlockDialog(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to block user:', error);
      }
      toast.error("Failed to block user");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Card className="glass-card overflow-hidden" data-testid={`community-post-${post.post_id}`}>
      <CardContent className="p-4">
        {/* Post header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#d4e4d4] flex items-center justify-center">
              <User className="w-5 h-5 text-[#5a8a5a]" />
            </div>
            <div>
              <p className="font-medium text-[#a69373]">{post.author_name || "Anonymous"}</p>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full ${iconColors}`}>
                  <PostIcon className="w-3 h-3 inline mr-1" />
                  {post.post_type}
                </span>
                <span className="text-xs text-[#a69373]/40">{timeAgo(post.created_at)}</span>
              </div>
            </div>
          </div>
          
          {/* Menu - only show for other users' posts */}
          {!isOwnPost && (
            <div className="relative">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0"
                onClick={() => setShowMenu(!showMenu)}
                data-testid={`post-menu-${post.post_id}`}
              >
                <MoreHorizontal className="w-4 h-4 text-[#a69373]/40" />
              </Button>
              
              {showMenu && (
                <div className="absolute right-0 top-8 bg-white rounded-lg shadow-lg border p-1 z-10 min-w-[140px]">
                  <button 
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[#a69373]/70 hover:bg-gray-100 rounded w-full"
                    onClick={() => {
                      setShowReportDialog(true);
                      setShowMenu(false);
                    }}
                    data-testid={`report-btn-${post.post_id}`}
                  >
                    <Flag className="w-4 h-4" />
                    Report
                  </button>
                  <button 
                    className="flex items-center gap-2 px-3 py-2 text-sm text-red-500/70 hover:bg-red-50 rounded w-full"
                    onClick={() => {
                      setShowBlockDialog(true);
                      setShowMenu(false);
                    }}
                    data-testid={`block-btn-${post.post_id}`}
                  >
                    <Ban className="w-4 h-4" />
                    Block User
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Post content */}
        <p className="text-[#a69373] mb-4 whitespace-pre-wrap">{post.content}</p>
        
        {/* Badge/milestone info if applicable */}
        {post.metadata?.badge_name && (
          <div className="bg-[#f5f9f5] rounded-lg p-3 mb-4">
            <p className="text-sm text-[#a69373]/60">
              Earned the <span className="font-medium text-[#5a8a5a]">{post.metadata.badge_name}</span> badge
            </p>
          </div>
        )}
        
        {post.metadata?.streak_days && (
          <div className="bg-[#f5f9f5] rounded-lg p-3 mb-4 flex items-center gap-2">
            <Flame className="w-5 h-5 text-orange-500" />
            <p className="text-sm text-[#a69373]/60">
              <span className="font-medium text-[#a69373]">{post.metadata.streak_days}</span> day streak
            </p>
          </div>
        )}
        
        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-black/5">
          <div className="flex items-center gap-4">
            <button 
              className={`flex items-center gap-1.5 text-sm transition-colors ${liked ? "text-red-500" : "text-[#a69373]/50 hover:text-red-500"}`}
              onClick={handleLike}
              data-testid={`like-btn-${post.post_id}`}
            >
              <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} />
              <span>{likeCount}</span>
            </button>
          </div>
          
          <ShareButton 
            type="reflection" 
            data={shareData}
            variant="icon"
          />
        </div>
      </CardContent>
      
      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-orange-500" />
              Report Post
            </DialogTitle>
            <DialogDescription>
              Help us understand what&apos;s wrong with this post. Your report will be reviewed by our team.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <RadioGroup value={reportReason} onValueChange={setReportReason}>
              {REPORT_REASONS.map((reason) => (
                <div key={reason.value} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <RadioGroupItem value={reason.value} id={reason.value} className="mt-0.5" />
                  <Label htmlFor={reason.value} className="cursor-pointer flex-1">
                    <span className="font-medium text-[#a69373]">{reason.label}</span>
                    <p className="text-xs text-[#a69373]/60 mt-0.5">{reason.description}</p>
                  </Label>
                </div>
              ))}
            </RadioGroup>
            
            {reportReason === "other" && (
              <Textarea
                placeholder="Please describe the issue..."
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
                className="mt-2"
                rows={3}
              />
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReport} 
              disabled={!reportReason || isSubmitting}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {isSubmitting ? "Submitting..." : "Submit Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Block User Dialog */}
      <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-red-500" />
              Block User
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to block <span className="font-medium">{post.author_name || "this user"}</span>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">What happens when you block someone:</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700">
                    <li>You won&apos;t see their posts in your community feed</li>
                    <li>They won&apos;t be notified that you blocked them</li>
                    <li>You can unblock them anytime from your profile settings</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBlockDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBlock} 
              disabled={isSubmitting}
              variant="destructive"
            >
              {isSubmitting ? "Blocking..." : "Block User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Create post dialog
function CreatePostDialog({ onPostCreated }) {
  const [content, setContent] = useState("");
  const [postType, setPostType] = useState("reflection");
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmit = async () => {
    if (!content.trim()) return;
    
    setIsSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/community/posts`,
        { content: content.trim(), post_type: postType },
        { withCredentials: true }
      );
      
      onPostCreated(response.data);
      setContent("");
      setPostType("reflection");
      setIsOpen(false);
      toast.success("Post shared with the community!");
    } catch {
      toast.error("Failed to create post");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Card className="glass-card cursor-pointer hover:border-[#5a8a5a]/30 transition-colors" data-testid="create-post-trigger">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#d4e4d4] flex items-center justify-center">
                <User className="w-5 h-5 text-[#5a8a5a]" />
              </div>
              <p className="text-[#a69373]/40">Share a reflection or celebrate a milestone...</p>
            </div>
          </CardContent>
        </Card>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Share with the Community</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Post type selector */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(POST_TYPE_ICONS).map(([type, Icon]) => (
              <button
                key={type}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${
                  postType === type 
                    ? POST_TYPE_COLORS[type] + " font-medium" 
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
                onClick={() => setPostType(type)}
              >
                <Icon className="w-4 h-4" />
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
          
          {/* Content textarea */}
          <Textarea
            placeholder={
              postType === "reflection" ? "What insight came to you today?" :
              postType === "milestone" ? "What milestone have you reached?" :
              postType === "encouragement" ? "Share some encouragement with others..." :
              "What would you like to share?"
            }
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            maxLength={500}
            className="resize-none"
          />
          
          <div className="flex justify-between items-center text-sm">
            <span className="text-[#a69373]/40">{content.length}/500</span>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="bg-[#5a8a5a] hover:bg-[#4a7a4a]"
          >
            {isSubmitting ? "Posting..." : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Post
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main community feed component
export function CommunityFeed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchPosts = useCallback(async (pageNum = 1) => {
    try {
      const response = await axios.get(`${API}/community/posts`, {
        params: { page: pageNum, limit: 10 },
        withCredentials: true
      });
      
      if (pageNum === 1) {
        setPosts(response.data.posts || []);
      } else {
        setPosts(prev => [...prev, ...(response.data.posts || [])]);
      }
      setHasMore(response.data.has_more || false);
    } catch (error) {
      toast.error("Failed to load community posts");
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch community posts:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleLike = async (postId, liked) => {
    await axios.post(
      `${API}/community/posts/${postId}/like`,
      { liked },
      { withCredentials: true }
    );
  };

  const handleReport = async (postId, reason, details) => {
    await axios.post(
      `${API}/community/posts/${postId}/report`,
      { reason, details },
      { withCredentials: true }
    );
  };

  const handleBlock = async (userId, userName) => {
    await axios.post(
      `${API}/community/block`,
      { user_id: userId },
      { withCredentials: true }
    );
    
    // Remove blocked user's posts from feed immediately
    setPosts(prev => prev.filter(post => post.user_id !== userId));
    toast.success(`${userName || "User"} has been blocked. You won't see their posts anymore.`);
  };

  const handleNewPost = (newPost) => {
    setPosts(prev => [newPost, ...prev]);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="glass-card animate-pulse">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gray-200" />
                <div className="space-y-2">
                  <div className="h-4 w-24 bg-gray-200 rounded" />
                  <div className="h-3 w-16 bg-gray-200 rounded" />
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full bg-gray-200 rounded" />
                <div className="h-4 w-3/4 bg-gray-200 rounded" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="community-feed">
      {/* Create post */}
      <CreatePostDialog onPostCreated={handleNewPost} />
      
      {/* Posts */}
      {posts.length === 0 ? (
        <Card className="glass-card">
          <CardContent className="p-8 text-center">
            <Sparkles className="w-12 h-12 text-[#d4e4d4] mx-auto mb-4" />
            <h3 className="text-[#a69373] font-heading text-lg mb-2">Be the first to share!</h3>
            <p className="text-[#a69373]/50 text-sm">
              Share a reflection, celebrate a milestone, or encourage others on their journey.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {posts.map(post => (
            <CommunityPost
              key={post.post_id}
              post={post}
              onLike={handleLike}
              onReport={handleReport}
              onBlock={handleBlock}
              currentUserId={user?.user_id}
            />
          ))}
          
          {hasMore && (
            <Button 
              variant="outline" 
              className="w-full"
              onClick={loadMore}
              data-testid="load-more-posts"
            >
              Load More
            </Button>
          )}
        </>
      )}
    </div>
  );
}

export default CommunityFeed;
